import { useBasket } from '~/context/basket-context';
import { toast } from 'sonner';
import { useCallback, useEffect, useState } from 'react';
import { trpc } from '~/trpc/client';
import { isTRPCClientError } from '~/trpc/is-trpc-error';
import { Reason } from '~/fecom-api/schema';
import { InvalidItemError } from '~/utils/custom-errors';

type UseAddToBasketParams = {
  productName: string;
  itemCode: string;
  quantity: number;
  showInBasket?: boolean;
  runOrderSimulate?: boolean;
  onSuccess?: () => void;
};

export const useAddToBasket = ({
  itemCode,
  quantity,
  productName,
  showInBasket,
  runOrderSimulate,
  onSuccess,
}: UseAddToBasketParams) => {
  const basket = useBasket();

  const addToBasket = trpc.basket.addToBasket.useMutation({
    onSuccess: () => {
      if (onSuccess) {
        onSuccess();
      }
    },
  });

  const itemInBasket = showInBasket
    ? basket.basket?.rows.find((row) => row.itemCode === itemCode)
    : null;
  const isInBasket = itemInBasket ? true : false;

  const [isAddingToBasket, setIsAddingToBasket] = useState(false);

  const handleAddToBasket = useCallback(async () => {
    if (addToBasket.isPending || isAddingToBasket) {
      return false;
    }
    setIsAddingToBasket(true);
    try {
      const exec = addToBasket.mutateAsync({
        itemCode,
        quantity,
      });
      const res = await toast
        .promise(exec, {
          id: `add-to-basket-${itemCode}`,
          loading: `Adding ${quantity > 1 ? `${quantity} × ` : ''} "${productName}" to basket...`,
          success: (res) => {
            const addedItem = res?.addedOrderLines?.at(0);
            const invalidItem = res?.invalidItems?.at(0);
            const name =
              itemCode === addedItem?.itemCode
                ? productName
                : addedItem?.itemCode;
            if (invalidItem) {
              throw new InvalidItemError(invalidItem.reason);
            }
            return `${addedItem && addedItem?.quantity > 1 ? `${addedItem?.quantity} × ` : ''} "${name}" added to basket`;
          },
          error: (error) => {
            if (isTRPCClientError(error)) {
              switch (error.data?.code) {
                case 'NOT_FOUND':
                  return `Product code "${productName}" not found`;
                case 'UNAUTHORIZED':
                  return `Product code "${productName}" is restricted`;
                case 'CONFLICT':
                  return `You've reached the limit of ${basket.basket?.hardBasketLimit ?? 0} 
                    individual products per transaction. To continue shopping, please 
                    complete this order and start a new basket for any additional items.`;
              }
            }
            if (error instanceof InvalidItemError) {
              switch (error.reason) {
                case Reason.IsVariantHeader:
                  return `Product code "${productName}" is not a valid product`;
                case Reason.NotFound:
                  return `Product code "${productName}" not found`;
                case Reason.Restricted:
                  return `Product code "${productName}" is restricted`;
                case Reason.IsTemporarilyUnavailable:
                  return `Product code "${productName}" is temporarily unavailable, please check back later`;
              }
            }
            return `Failed to add "${productName}" to basket`;
          },
        })
        .unwrap();

      if (res) {
        await basket.actions?.reload({
          runOrderSimulation: runOrderSimulate,
        });
      }
      setIsAddingToBasket(false);
      return true;
    } catch (error) {
      if (error instanceof Error) {
        console.error(error.message);
      }
      setIsAddingToBasket(false);
      return false;
    }
  }, [
    addToBasket,
    basket.actions,
    basket.basket?.hardBasketLimit,
    isAddingToBasket,
    itemCode,
    productName,
    quantity,
    runOrderSimulate,
  ]);

  useEffect(() => {
    return () => {
      toast.dismiss(`add-to-basket-${itemCode}`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    handleAddToBasket,
    isInBasket,
    itemInBasket,
    isAddingToBasket,
    isInitalLoading: basket.isInitialLoading,
  };
};
